/**
 * rw-free-gift.js — put the free Quarry Claw Patch in the cart by itself.
 *
 * Shopify's "buy X get Y" discount does NOT add Y to the cart; it only zeroes Y once the
 * customer has put it there. Measured on the live store 2026-08-11: a qualifying cart with no
 * patch in it gets nothing, and the announcement-bar link was the only route to the gift. This
 * closes that gap.
 *
 * ── THE RULE THIS MIRRORS ────────────────────────────────────────────────────────────────────
 * The discount is "buy $50 of THESE ELEVEN PRODUCTS, get 1 patch free". Two measured facts
 * decide the logic, and getting either wrong CHARGES the customer $9.99 for a "free" gift:
 *
 *   1. The $50 must come from the buy-list. A $77.99 cart of tee + cap does NOT free the patch,
 *      because merch is not on the list. So QUALIFYING below is the buy-list, not the catalogue.
 *   2. BXGY reads the PRE-discount subtotal. A lone $64.99 Blacktop still frees the patch even
 *      though NEIGHBOR25 drops the cart to $48.75. So we sum original_line_price, never final.
 *
 * 🔑 QUALIFYING AND THRESHOLD MUST BE KEPT IN SYNC WITH THE DISCOUNT BY HAND. If a product is
 * added to or removed from the buy-list in Shopify and not here, this adds a patch that will not
 * be zeroed. Verified safe against the live discount on 2026-08-11.
 *
 * ── SAFETY ───────────────────────────────────────────────────────────────────────────────────
 * The dangerous state is a patch sitting in a cart that no longer qualifies: the discount stops
 * applying and the customer is quietly billed for it. So the gift is removed again the moment
 * the cart drops below the bar. To do that we must know which patch WE added versus one the
 * customer chose to buy, and we never touch theirs. Two independent signals, because losing the
 * marker is exactly the failure that bills someone:
 *   - a line property, which also prints on the packing slip so fulfilment can see it is the freebie
 *   - the line key recorded in sessionStorage, which needs no support from the cart at all
 *
 * If the customer deletes the gift themselves we take the hint and stop re-adding it for the rest
 * of the session, rather than fighting them for an item they cannot get rid of.
 *
 * Every failure path is "do nothing". The patch stays purchasable by hand, exactly as before.
 */

import { StandardEvents } from '@shopify/events';

const GIFT_VARIANT = 45676995150031;
const GIFT_HANDLE = 'quarry-claw-patch';
const THRESHOLD_CENTS = 5000;
const PROP_KEY = 'Free gift';
const PROP_VALUE = 'Yes';
const STORE_KEY = 'rw_free_gift';

/** the discount's buy-list, verbatim. NOT the catalogue — merch is deliberately absent. */
const QUALIFYING = new Set([
  'ridgeline-molle-harness',
  'blacktop-everyday-harness',
  'anchor-tactical-collar',
  'slackline-bungee-lead',
  'duststorm-trail-goggles',
  'airframe-elevated-cot',
  'coldfront-cooling-vest',
  'gauntlet-training-tug',
  'ration-rubber-chew',
  'canteen-trail-bottle',
  'whitewater-life-vest',
  'truck-suv-ramp',
]);

/**
 * Opt-in tracing. Everything here fails silently on purpose so it can never break a customer's
 * cart — which also means a fault leaves no trace. Turn it on by loading any page with
 * `?rw_gift_debug=1` (it sticks until `?rw_gift_debug=0`) and watch the console.
 */
const DEBUG = (() => {
  try {
    const q = new URLSearchParams(location.search).get('rw_gift_debug');
    if (q === '1') localStorage.setItem('rw_gift_debug', '1');
    if (q === '0') localStorage.removeItem('rw_gift_debug');
    return localStorage.getItem('rw_gift_debug') === '1';
  } catch { return false; }
})();
const log = (...a) => { if (DEBUG) console.log('[rw-free-gift]', ...a); };

/** sessionStorage is unavailable in some privacy modes; degrade to the line property alone. */
const store = {
  get() { try { return sessionStorage.getItem(STORE_KEY) || ''; } catch { return ''; } },
  set(v) { try { sessionStorage.setItem(STORE_KEY, v); } catch { /* no-op */ } },
};

let busy = false;

const isGift = (line, savedKey) =>
  line.properties?.[PROP_KEY] === PROP_VALUE || (!!savedKey && line.key === savedKey);

async function readCart() {
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

async function evaluate() {
  // Our own updateCart re-emits the event we listen for. The guard makes that terminate rather
  // than recurse; the state checks below make a second pass a no-op anyway.
  if (busy) return;
  busy = true;
  try {
    const actions = window.Shopify?.actions;
    if (!actions?.updateCart) { log('no Shopify.actions.updateCart — cannot run'); return; }

    const cart = await readCart();
    const saved = store.get();
    const patches = cart.items.filter((i) => i.handle === GIFT_HANDLE);
    const gift = patches.find((i) => isGift(i, saved));

    const qualifying = cart.items
      .filter((i) => QUALIFYING.has(i.handle))
      .reduce((sum, i) => sum + i.original_line_price, 0);
    const qualifies = qualifying >= THRESHOLD_CENTS;

    // Learn the key of our own gift whenever we see it, not only when we add it. sessionStorage
    // is per-TAB but the cart is shared, so a second tab opens onto a cart that already holds the
    // gift with no record of it. Without this, the first delete in that tab looks like "they never
    // had one" and we hand it straight back — the customer has to delete twice. Measured 2026-08-11.
    if (gift && store.get() !== gift.key) store.set(gift.key);

    log('evaluate', {
      lines: cart.items.map((i) => `${i.handle} x${i.quantity} @${i.original_line_price}`),
      qualifyingCents: qualifying,
      thresholdCents: THRESHOLD_CENTS,
      qualifies,
      patchesInCart: patches.length,
      haveGift: !!gift,
      saved: store.get(),
    });

    if (qualifies && patches.length === 0) {
      // A stored value here means we already put one in and it is gone now — either the
      // customer deleted it, or they deleted it earlier and we recorded that. Their call
      // either way; stop offering for the rest of the session.
      if (saved) { log('gift was removed by the customer — not re-adding this session'); store.set('dismissed'); return; }

      log('ADDING the gift patch');
      await actions.updateCart({
        lines: [{
          merchandiseId: GIFT_VARIANT,
          quantity: 1,
          attributes: [{ key: PROP_KEY, value: PROP_VALUE }],
        }],
      });

      // Record the key the line actually landed with, so removal works even if the property
      // did not survive the round trip.
      try {
        const after = await readCart();
        const added = after.items.find((i) => i.handle === GIFT_HANDLE);
        if (added) store.set(added.key);
      } catch { /* the property is still there as a fallback signal */ }
      return;
    }

    // The billing risk: a gift left behind in a cart that no longer earns it.
    if (!qualifies && gift) {
      log('REMOVING the gift — cart no longer qualifies');
      await actions.updateCart({ lines: [{ id: gift.key, quantity: 0 }] });
      store.set('');
    }
  } catch (err) {
    // Never let this break a cart. Doing nothing leaves the patch purchasable by hand.
    log('FAILED', err);
  } finally {
    busy = false;
  }
}

function start() {
  log('start', { hasActions: !!window.Shopify?.actions, designMode: !!window.Shopify?.designMode });

  // The theme editor lets a merchant fiddle with a cart that is not a real shopper's.
  if (window.Shopify?.designMode) return;

  // Standard events bubble, so document catches them wherever they are dispatched — the
  // store's standard-actions-override retargets them at the cart drawer.
  //
  // 🔑 THE EVENT IS OPTIMISTIC. Horizon's product form dispatches CartLinesUpdateEvent BEFORE
  // it POSTs to cart/add, and hands the real completion over on `event.promise`. Reading
  // cart.js straight off the event therefore sees the cart as it was BEFORE the item landed,
  // so a cart that just crossed the threshold still looks too small and no gift is added.
  // Wait for the promise. Resolve or reject, re-check either way: a failed add just means the
  // cart is unchanged and evaluate() is a no-op.
  document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
    log('cartLinesUpdate seen', { hasPromise: !!event?.promise });
    try { await event?.promise; } catch { /* add failed; re-check anyway */ }
    evaluate();
  });

  // A returning shopper can arrive already over the line with no event to react to. Skip the
  // request entirely when there is no cart cookie yet.
  if (/(?:^|;\s*)cart=/.test(document.cookie)) evaluate();
}

if (window.Shopify?.actions) start();
else document.addEventListener('DOMContentLoaded', start, { once: true });
